/**
 * @module loadClientConfiguration
 */

import { action, orchestrator } from 'satcheljs';
import { trace } from 'owa-trace';
import { localStorageExists, setItem } from 'owa-local-storage';
import { ClientConfigurationRequestState } from '../store/schema/clientConfiguration';
import ClientConfigurationCache, {
    ClientConfigurationCacheState,
} from '../store/schema/clientConfigurationCache';
import updateRequestState from '../mutators/updateRequestState';
import makeClientConfigurationRequest from '../service/makeClientConfigurationRequest';
import setClientConfiguration from '../mutators/setClientConfiguration';
import { CLIENT_CONFIGURATION_LOCAL_STORAGE_KEY } from '../constants';
import getThemeDefinitions from '../selectors/getThemeDefinitions';
import { loadThemeDefinitions } from 'owa-actionable-message-v2';
import { isFeatureEnabled } from 'owa-feature-flags';
import getClientConfigurationFromStore from '../selectors/getClientConfigurationFromStore';

/**
 * @function
 * To build cache data out of the response and its status
 * @param {ClientConfigurationCacheState} state - Status of the response
 * @param {any} [data= null] - The actual configuration which is returned by the Actionable Message Service (null be default)
 * @returns A structured cache data
 */

const buildClientConfigurationCache = (
    state: ClientConfigurationCacheState,
    data: any = null
): ClientConfigurationCache => {
    const cache = {
        data: data,
        state: state,
        time: Date.now(),
    };
    if (data === null) {
        const configFromStore = getClientConfigurationFromStore();
        cache.data = configFromStore.data;
    }
    return cache;
};

/**
 * @function
 * To set the client configuration in local storage
 * @param {ClientConfigurationCache} configData - The configuration data object
 * @returns nothing
 */

const setClientConfigurationInLocalStorage = (configData: ClientConfigurationCache) => {
    if (!localStorageExists(window)) {
        return;
    }
    try {
        setItem(window, CLIENT_CONFIGURATION_LOCAL_STORAGE_KEY, JSON.stringify(configData));
    } catch (error) {
        trace.info(
            '[ActionableMessageDebug] owa-actionable-message-connectors loadClientConfiguration setClientConfigurationInLocalStorage Exception on setItem|JSON.stringify ' +
                error.toString()
        );
    }
};

/**
 * @action
 * A dummy action for an orchestrator which is making request to the service and saving it in the memory as well as in the localStorage
 */

const loadClientConfiguration = action('loadClientConfiguration');

/**
 * @orchestrator
 * An orchestrator to load the client configuration by making the service call and making sure its saved in memory as well as in the cache
 */

orchestrator(loadClientConfiguration, async () => {
    updateRequestState(ClientConfigurationRequestState.InProgress);
    let configData = null;
    try {
        const response = await makeClientConfigurationRequest();
        configData = buildClientConfigurationCache(ClientConfigurationCacheState.Success, response);
    } catch (error) {
        configData = buildClientConfigurationCache(ClientConfigurationCacheState.Error);
    } finally {
        setClientConfiguration(configData);
        setClientConfigurationInLocalStorage(configData);
        updateRequestState(ClientConfigurationRequestState.Resolved);
        if (isFeatureEnabled('rp-actionableMessagesThemeConfig')) {
            const themes = getThemeDefinitions();
            loadThemeDefinitions(themes);
        }
    }
});

export default loadClientConfiguration;
